import type { AccountResponse } from "@esi/shared";
import { useQueryClient } from "@tanstack/react-query";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { InlineError, Notice } from "@/components/Notice";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { api } from "../api.ts";
import { ApiError, needsReauth, ok } from "../api-errors.ts";
import { t as translate } from "../i18n/i18n.ts";
import type { Secure } from "../security/step-up.tsx";
import { useSession } from "../session.tsx";
import { SubHeading } from "../settings-shell/PageSection.tsx";
import { accountKeys, csrf, done } from "./queries.ts";

/**
 * Whether the account page shows ESI sign-in: held, or allowed by the person's domain now and
 * this session came through the IdP (adding one needs a recent SSO sign-in; a native session,
 * such as an ESI account whose domain was later bound to an IdP, could never send the link).
 */
export const showsEsiCredential = (account: AccountResponse) =>
  account.esiCredential !== undefined &&
  (account.esiCredential.held || (account.esiCredential.allowed && account.federated));

/**
 * An ESI password or passkey next to the person's SSO (openspec change
 * `sso-and-esi-credentials`, D4, 3.5): "Add a password or passkey" when the domain allows it
 * (the set-password link goes by e-mail; adding it needs an SSO sign-in within 5 minutes), the
 * held state with Remove (a recent sign-in), and the state of a credential the domain no longer
 * allows (kept but refused, D7).
 */
export function EsiCredentialSection({
  account,
  secure,
}: {
  account: AccountResponse;
  secure: Secure;
}) {
  const { t } = useTranslation();
  const id = useId();
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  /** The link went: one is enough (a second is refused while it lives). */
  const [sent, setSent] = useState(false);
  const state = account.esiCredential;
  if (!state) {
    return null;
  }

  const act = (work: () => Promise<void>) => async () => {
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      // The refusal is shown here, also when the step-up runs the action again (it holds this).
      await secure(async () => {
        try {
          await work();
        } catch (e) {
          if (needsReauth(e)) {
            throw e;
          }
          setError(refusal(e));
        }
      });
    } finally {
      setBusy(false);
    }
  };

  const add = act(async () => {
    const link = await ok(
      await api.api.account["esi-credential"].$post(undefined, csrf(csrfToken)),
    );
    setSent(true);
    setStatus(t("esiCredential.sent", { email: link.sentTo }));
  });
  const remove = act(async () => {
    await done(await api.api.account["esi-credential"].$delete(undefined, csrf(csrfToken)));
    setConfirming(false);
    setStatus(t("esiCredential.removed"));
    await queryClient.invalidateQueries({ queryKey: accountKeys.account });
  });

  return (
    <div data-esi-content="people" className="flex max-w-xl flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <SubHeading id={`${id}-heading`}>{t("esiCredential.title")}</SubHeading>
        <Badge variant={state.held && state.allowed ? "secondary" : "outline"}>
          {!state.held
            ? t("esiCredential.off")
            : state.allowed
              ? t("esiCredential.on")
              : t("esiCredential.refusedBadge")}
        </Badge>
      </div>
      {state.held && !state.allowed ? (
        <Notice tone="warning">{t("esiCredential.notAllowed")}</Notice>
      ) : (
        <p className="text-sm leading-relaxed text-fg-secondary">
          {state.held ? t("esiCredential.heldLead") : t("esiCredential.addLead")}
        </p>
      )}
      {error ? <InlineError>{error}</InlineError> : null}
      {state.held ? (
        confirming ? (
          <div className="flex flex-col gap-3 rounded-lg border border-line p-3">
            <p className="text-sm font-medium text-fg">{t("esiCredential.removeTitle")}</p>
            <p className="text-sm text-fg-secondary">
              {account.federated
                ? t("esiCredential.removeBody")
                : `${t("esiCredential.removeBody")} ${t("esiCredential.removeSignsOut")}`}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="destructive"
                disabled={busy}
                onClick={() => void remove()}
              >
                {busy ? t("esiCredential.removing") : t("esiCredential.remove")}
              </Button>
              <Button type="button" variant="ghost" onClick={() => setConfirming(false)}>
                {t("app.cancel")}
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setError(null);
                setConfirming(true);
              }}
            >
              {t("esiCredential.removeEllipsis")}
            </Button>
          </div>
        )
      ) : sent ? null : (
        <div>
          <Button type="button" disabled={busy} onClick={() => void add()}>
            {busy ? t("esiCredential.sending") : t("esiCredential.add")}
          </Button>
        </div>
      )}
      <p role="status" className="text-sm text-success-fg">
        {status}
      </p>
    </div>
  );
}

/** A refused add or remove, in plain words. */
function refusal(error: unknown): string {
  const t = translate;
  if (!(error instanceof ApiError)) {
    return t("errors.network");
  }
  if (error.status === 429 || error.code === "throttled") {
    return t("esiCredential.refused.throttled");
  }
  if (error.status === 403) {
    return t("esiCredential.refused.notAllowed");
  }
  if (error.code === "conflict") {
    return t("esiCredential.refused.conflict");
  }
  return error.message;
}
